import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Plus, X, Clock, CheckCircle2, XCircle, Tag, Gift, Percent, Ticket, ExternalLink, Copy, Sparkles, Wine, Calendar } from "lucide-react";

const TYPES = [
  { id: "discount", label: "Réduction", icon: Percent, color: "bg-rose-100 text-rose-700" },
  { id: "promo_code", label: "Code promo", icon: Ticket, color: "bg-purple-100 text-purple-700" },
  { id: "free_trial", label: "Essai gratuit", icon: Gift, color: "bg-emerald-100 text-emerald-700" },
  { id: "perk", label: "Avantage", icon: Sparkles, color: "bg-amber-100 text-amber-700" },
];

function emptyForm() {
  return { title: "", description: "", type: "discount", value: "", promo_code: "", url: "", conditions: "", expires_at: "", cover_image: "", is_public: false };
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "En attente", icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
    published: { label: "Publiée", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    rejected: { label: "Rejetée", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  };
  const m = map[status] || map.pending;
  const I = m.icon;
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${m.cls}`} data-testid={`club-status-${status}`}><I className="h-2.5 w-2.5" />{m.label}</span>;
}

function OfferCard({ offer }) {
  const T = TYPES.find((t) => t.id === offer.type) || TYPES[0];
  const Icon = T.icon;
  const copy = () => { navigator.clipboard.writeText(offer.promo_code); toast.success("Code copié dans le presse-papier"); };
  const expired = offer.expires_at && new Date(offer.expires_at) < new Date();
  return (
    <div className={`relative overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:shadow-md ${expired ? "border-neutral-200 opacity-60" : "border-neutral-100"}`} data-testid={`club-offer-${offer.id}`}>
      {offer.cover_image ? (
        <img src={offer.cover_image} alt={offer.title} className="aspect-[16/9] w-full object-cover" />
      ) : (
        <div className={`grid aspect-[16/9] w-full place-items-center ${T.color}`}>
          <Icon className="h-16 w-16 opacity-50" />
        </div>
      )}
      {expired && <div className="absolute right-3 top-3 rounded-full bg-rose-600 px-2.5 py-1 text-[10px] font-bold uppercase text-white">Expirée</div>}
      <div className="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${T.color}`}><Icon className="h-3 w-3" />{T.label}</span>
          {offer.value && <span className="rounded-full bg-wine-600 px-2.5 py-1 text-xs font-bold text-white">{offer.value}</span>}
        </div>
        <h3 className="mt-2 font-serif text-lg font-semibold text-navy">{offer.title}</h3>
        {offer.company && (
          <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-neutral-500">
            {offer.company.logo && <img src={offer.company.logo} alt={offer.company.name} className="h-4 w-4 rounded-full object-cover" />}
            <span>par <strong className="text-neutral-700">{offer.company.name}</strong></span>
          </div>
        )}
        <p className="mt-2 line-clamp-3 text-sm text-neutral-600">{offer.description}</p>
        {offer.conditions && <p className="mt-2 text-[11px] italic text-neutral-500">⚠️ {offer.conditions}</p>}
        {offer.expires_at && !expired && (
          <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-amber-700"><Calendar className="h-3 w-3" />Valide jusqu&apos;au {new Date(offer.expires_at).toLocaleDateString("fr-FR")}</p>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {offer.promo_code && (
            <button onClick={copy} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-wine-300 bg-wine-50/50 px-3 py-1.5 font-mono text-xs font-bold text-wine-800 hover:bg-wine-50" data-testid={`club-copy-${offer.id}`}>
              <Copy className="h-3 w-3" />{offer.promo_code}
            </button>
          )}
          {offer.url && <a href={offer.url} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-wine-600" data-testid={`club-go-${offer.id}`}><ExternalLink className="h-3 w-3" />En profiter</a>}
        </div>
      </div>
    </div>
  );
}

export default function MemberClub() {
  const [tab, setTab] = useState("browse");
  const [offers, setOffers] = useState([]);
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    api.get("/club").then((r) => setOffers(r.data || [])).catch(() => {});
    api.get("/portal/club/my").then((r) => setMine(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.url && !form.promo_code) {
      toast.error("Ajoutez une URL ou un code promo");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/portal/club/submit", form);
      toast.success("Offre soumise — l'admin va l'examiner sous peu");
      setForm(emptyForm());
      setOpen(false);
      load();
      setTab("mine");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la soumission");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalLayout sidebar="member">
      <div className="overflow-hidden rounded-3xl border border-wine-200 bg-gradient-to-br from-wine-50 via-amber-50 to-white p-6 md:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-wine-100 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-wine-700"><Wine className="h-3 w-3" />Avantages réservés aux membres</div>
            <h1 className="mt-3 text-3xl font-bold text-navy md:text-5xl">Le Club</h1>
            <p className="mt-2 max-w-2xl text-neutral-700">Les meilleures offres, réductions et essais gratuits — partagés entre membres de La WineTech. Proposez les vôtres et gagnez <strong>+5 points</strong> par offre validée.</p>
          </div>
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-wine-600 px-5 py-3 text-sm font-bold text-white shadow-sm hover:bg-wine-700" data-testid="club-submit-btn">
            <Plus className="h-4 w-4" />Proposer une offre
          </button>
        </div>
      </div>

      <div className="mt-6 inline-flex gap-1 rounded-full bg-neutral-100 p-1">
        <button onClick={() => setTab("browse")} className={`rounded-full px-4 py-2 text-xs font-bold ${tab === "browse" ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid="club-tab-browse">
          <Tag className="mr-1 inline h-3.5 w-3.5" />Toutes les offres ({offers.length})
        </button>
        <button onClick={() => setTab("mine")} className={`rounded-full px-4 py-2 text-xs font-bold ${tab === "mine" ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid="club-tab-mine">
          Mes offres {mine.length > 0 && <span className="ml-1 rounded-full bg-wine-100 px-1.5 py-0.5 text-[10px] text-wine-700">{mine.length}</span>}
        </button>
      </div>

      {tab === "browse" && (
        <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {offers.length === 0 && <div className="md:col-span-3 rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500" data-testid="club-empty">Aucune offre pour le moment. Soyez le premier à en proposer une !</div>}
          {offers.map((o) => <OfferCard key={o.id} offer={o} />)}
        </div>
      )}

      {tab === "mine" && (
        <div className="mt-6 space-y-3">
          {mine.length === 0 && <div className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500" data-testid="club-mine-empty">Vous n&apos;avez encore proposé aucune offre.</div>}
          {mine.map((o) => {
            const T = TYPES.find((t) => t.id === o.type) || TYPES[0];
            return (
              <div key={o.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-neutral-100 bg-white p-4" data-testid={`club-mine-${o.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-navy">{o.title}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${T.color}`}>{T.label}</span>
                    <StatusBadge status={o.status} />
                    {o.is_public && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">Visible public</span>}
                  </div>
                  <div className="text-xs text-neutral-500">{o.value} {o.promo_code && `· code ${o.promo_code}`} · soumise le {new Date(o.created_at).toLocaleDateString("fr-FR")}</div>
                  {o.status === "rejected" && o.rejected_reason && <div className="mt-1 text-xs text-rose-600">Raison du rejet : {o.rejected_reason}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="club-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy"><Wine className="mr-1 inline h-5 w-5 text-wine-500" />Proposer une offre au Club</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1 text-xs text-neutral-500">Votre offre sera examinée par un admin. Une fois validée, vous gagnez <strong>+5 points</strong>.</p>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre (ex: -30% sur l'audit énergétique)" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="club-title" />
              <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description de l'offre" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="club-description" />
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Type d&apos;offre</label>
                <div className="mt-1 grid grid-cols-2 gap-2 md:grid-cols-4">
                  {TYPES.map((t) => {
                    const I = t.icon;
                    const active = form.type === t.id;
                    return (
                      <button key={t.id} type="button" onClick={() => setForm({ ...form, type: t.id })} className={`inline-flex flex-col items-center gap-1 rounded-xl border p-3 text-xs font-bold transition ${active ? "border-wine-500 bg-wine-50 text-wine-700" : "border-neutral-200 bg-white text-neutral-600 hover:border-wine-300"}`} data-testid={`club-type-${t.id}`}>
                        <I className="h-5 w-5" />{t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="Valeur (ex: -30%, 1 mois gratuit)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="club-value" />
              <input value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value })} placeholder="Code promo (optionnel)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" data-testid="club-promo-code" />
              <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="URL pour bénéficier de l'offre" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="club-url" />
              <textarea rows={2} value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} placeholder="Conditions / mentions légales (optionnel)" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="club-conditions" />
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Date d&apos;expiration (optionnel)</label>
                <input type="date" value={form.expires_at?.split("T")[0] || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="club-expires" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Image de couverture</label>
                <ImageUpload value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} label="" testId="club-cover" />
              </div>
              <label className="md:col-span-2 inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50/50 px-3 py-2 text-xs text-neutral-700">
                <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="accent-wine-500" data-testid="club-is-public" />
                <span><strong>Offre publique</strong> — visible aussi des visiteurs non connectés (lead magnet). À utiliser pour les offres marketing.</span>
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" disabled={submitting} className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="club-submit-confirm">
                {submitting ? "Envoi..." : "Soumettre pour validation"}
              </button>
            </div>
          </form>
        </div>
      )}
    </PortalLayout>
  );
}
